/**
 * Which query key a connection's database listing belongs under — the sharing
 * policy for the ONE slot every picker reads.
 *
 * Its own module rather than a corner of `useDatabaseList`: the rule is pure
 * data, and the non-React readers in `useDatabaseQueries`
 * (`readDatabaseListingThroughCache`) need it without importing a hook.
 */

import type { ConnectionStatus } from '../../connections/models/connectionTypes';
import { isProbeConnectionId } from '../../../types/probeConnectionId';
import { databaseObjectsQueryKey } from './databaseObjectsQueryKeys';

/**
 * "Is this connect still live?" — the ONE definition of live, shared by the
 * sharing rule below and by `connectionListingLifecycle`'s snapshot, so the two
 * halves of the retention contract (who may WRITE the shared slot, and when it is
 * DROPPED) can never disagree about what live means.
 *
 * `'error'` counts as live: the `activeConnections` entry is still there and its
 * pool may well still be open — a status flap is not the end of a connect. Only
 * `'disconnected'`, or being absent from `activeConnections` altogether, ends one.
 */
export function isLiveConnectStatus(status: ConnectionStatus | string | undefined): boolean {
  return status !== undefined && status !== 'disconnected';
}

/** The minimum a store entry has to carry for {@link isConnectionLive}. */
type LiveConnectionProbe = { id: string; status: ConnectionStatus | string };

/**
 * Does `connectionId` have a live connect in `activeConnections` right now?
 * Pure, so both the hook (reactive, from its store selector) and the non-React
 * reader (`useConnectionStore.getState()`) ask the same question.
 */
export function isConnectionLive(
  activeConnections: ReadonlyArray<LiveConnectionProbe> | undefined,
  connectionId: string | undefined,
): boolean {
  if (!connectionId) return false;
  const entry = activeConnections?.find((connection) => connection.id === connectionId);
  return !!entry && isLiveConnectStatus(entry.status);
}

/**
 * Everything the sharing decision needs that the connection id cannot tell us.
 *
 * `connectionIsLive` is REQUIRED rather than defaulted: a caller that forgot it
 * would silently write the session-retained shared slot from outside a live
 * connect, which is exactly the class of bug this parameter exists to prevent.
 */
export type DatabaseListSharingContext = {
  /** {@link isConnectionLive} for this connection, as of now. */
  connectionIsLive: boolean;
  /** `true` for the Add/Edit Connection dialog's UNLIMITED listing. */
  ignoreConnectionLimit?: boolean;
};

/**
 * Listings that must NEVER touch the shared per-connection slot.
 *
 * - A connection-dialog probe id (`probe:<uuid>:<liveId>`) is minted fresh on
 *   every click, so caching one would grow the cache per click and never be
 *   read back. `databasesService.recordLastListingAdapter` already refuses
 *   probe ids for the same reason.
 * - `ignoreConnectionLimit: true` returns the UNLIMITED list (only the
 *   Add/Edit Connection dialog asks for it, and `NewConnectionPopup`'s edit
 *   path asks with the LIVE connection id). Sharing a slot would leak that
 *   list into every limit-respecting picker.
 * - A connection with **no live connect**. The shared slot is retained for the
 *   whole session (`gcTime: Infinity`) and bounded only by "the connect it was
 *   taken during ended", so a listing taken outside a live connect would have no
 *   end of life at all: `connectionListingLifecycle` cannot fire a transition for
 *   a connect that never happened. Worse, what a disconnected consumer writes is
 *   usually a FAILURE (`NO_POOL`, stale credentials), and the Explorer's own
 *   on-connect listing reads through `fetchQuery` with a 30 s `staleTime` — so a
 *   connection that had just connected successfully was served that cached
 *   failure and painted the error tile.
 *
 * Not-live listings are ROUTED to the unshared key rather than refused, so every
 * not-connected surface behaves exactly as it did: it still performs a real
 * listing and still reports the real error, but under a key no picker and no
 * producer reads, with `gcTime: 0`. A later connect therefore finds the shared
 * slot empty.
 *
 * Which surfaces that actually affects:
 *   - **Data Import's Target step** deliberately offers saved-but-disconnected
 *     connections. Picking one runs the app's real connect flow first
 *     (`DestinationConnectionSelect` → `useInlineConnect`) and only calls
 *     `onSelectConnection` once it succeeds, so the cascade is normally handed a
 *     LIVE id; it lazily connects rather than listing a dead connection. What it
 *     can still see not-live is a restored draft target or a mid-session
 *     disconnect — and for those the unshared route keeps the field's "Couldn't
 *     list databases." + Retry exactly as it is today. Gating the hook the way
 *     `AddLinkedDbDialog` does would turn that into a silent "No databases
 *     found", which is why this surface routes instead of being gated.
 *   - **Any picker whose connection disconnects while it stays mounted** — its
 *     key moves off the shared slot instead of re-creating the entry behind the
 *     lifecycle eviction's back.
 *
 * Exported so the rule lives in one place: a future caller that wants to reuse
 * the key asks here rather than re-deriving the exemption.
 */
export function canShareDatabaseListing(
  connectionId: string | undefined,
  context: DatabaseListSharingContext,
): boolean {
  if (!connectionId) return false;
  if (isProbeConnectionId(connectionId)) return false;
  if (context.ignoreConnectionLimit) return false;
  if (!context.connectionIsLive) return false;
  return true;
}

/**
 * The query key a listing for `connectionId` belongs under: the shared slot
 * when {@link canShareDatabaseListing} allows it, otherwise a per-connection
 * key outside the `['database-objects', …]` namespace that no picker reads —
 * paired with `gcTime: 0` at the call site so the entry is dropped the moment
 * nothing observes it.
 */
export function databaseListQueryKey(
  connectionId: string | undefined,
  context: DatabaseListSharingContext,
): readonly unknown[] {
  if (canShareDatabaseListing(connectionId, context)) {
    return databaseObjectsQueryKey.databases(connectionId as string);
  }
  return ['database-objects-unshared', 'databases', connectionId ?? '', context.ignoreConnectionLimit === true] as const;
}
